"use client";
/**
 * One-time seed: clone "Macbook Pro - Retina Screen" into a scrollable
 * laptop arrangement that keeps the Samsung 49 inventory on every page.
 *
 * The ultrawide is the source of WHAT is on each page (same leaves, same
 * extra RGL panes). The clone wraps wide rows so a ~1300 CSS-px Retina
 * screen can scroll instead of compressing six charts into postage stamps.
 * Idempotent — a later load does not rewrite a profile the user has already
 * been given at this seed generation.
 */
import {
  loadProfiles,
  saveProfiles,
  type DisplayProfile,
} from "./profiles";
import {
  namespaceLogicalKeys,
  rawGet,
  readNamespaced,
  seedNamespaceFrom,
  writeNamespaced,
} from "./layoutNamespace";
import { loadPages, treeKey, type WsNode } from "@/lib/workspace/workspacePages";
import {
  flattenComponents,
  isLaptopGrid,
  reflowForLaptop,
  reflowGridLayout,
  suggestedGridRows,
  suggestedLaptopCols,
  type LaptopGrid,
} from "@/lib/workspace/reflowLaptop";

export const RETINA_ARRANGED_NAME = "Macbook Pro - Retina arranged";
/** Bump to re-seed trees + page grids for users who have not customized this clone. */
export const RETINA_TREE_SEED = 2;

/**
 * Every shipped PanelWorkspacePage / brief grid. Also discovered from the
 * source / Samsung namespaces so a page the user added later is not skipped.
 */
export const PAGE_GRID_KEYS = [
  "mpp:dashboard:pw:v1",
  "mpp:dashboard-preview:pw:v3",
  "mpp:top-down:pw:v1",
  "mpp:bucket-charts:pw:v1",
  "mpp:breadth-2:pw:v2",
  "mpp:breadth-3:pw:v1",
  "mpp:daily-brief:v2",
  "mpp:live-breadth:pw:v1",
  "mpp:live-breadth-preview:pw:v1",
  "mpp:timing-brief-preview:pw:v2",
  "mpp:breadth-1-preview:pw:v1",
  "mpp:breadth-2-preview:pw:v1",
  "mpp:breadth-3-preview:pw:v1",
  "mpp:index-etfs-preview:pw:v1",
  "mpp:commodities-etfs-preview:pw:v1",
  "mpp:guppy-preview:pw:v1",
  "mpp:ratio-preview:pw:v2",
  "mpp:macro-preview:pw:v1",
  "mpp:outlook-preview:pw:v1",
  "mpp:scanner-preview:pw:v3",
  "mpp:vpa-preview:pw:v2",
  "mpp:charting-preview:pw:v1",
  "mpp:seasonality-preview:pw:v1",
  "mpp:fundamentals-preview:pw:v1",
  "mpp:options-trade-preview:pw:v1",
  "mpp:options-analyze-preview:pw:v1",
  "mpp:options-flow-preview:pw:v1",
  "mpp:options-scan-preview:pw:v1",
  "mpp:bets-preview:pw:v1",
  "mpp:backtest-preview:pw:v1",
  "mpp:wealth-overview-preview:pw:v1",
  "mpp:wealth-spending-preview:pw:v1",
  "mpp:wealth-investments-preview:pw:v1",
  "mpp:wealth-accounts-preview:pw:v1",
  "mpp:wealth-property-preview:pw:v2",
  "mpp:wealth-trades-dashboard-preview:pw:v1",
  "mpp:wealth-trades-book-preview:pw:v1",
  "mpp:wealth-trades-log-preview:pw:v1",
  "mpp:wealth-trades-open-preview:pw:v1",
  "mpp:wealth-trades-reports-preview:pw:v1",
  "mpp:wealth-trades-tax-preview:pw:v1",
  "mpp:wealth-trades-planning-preview:pw:v1",
  "mpp:wealth-trades-journal-preview:pw:v1",
  "mpp:wealth-forecast-preview:pw:v1",
  "mpp:settings-data-feed-preview:pw:v1",
  "mpp:settings-breadth-preview:pw:v1",
  "mpp:settings-indicators-preview:pw:v1",
  "mpp:settings-theme-preview:pw:v1",
  "mpp:settings-backup-preview:pw:v1",
  "mpp:settings-universe-preview:pw:v1",
  "mpp:settings-pages-preview:pw:v1",
  "mpp:settings-maintenance-preview:pw:v1",
  "mpp:help-preview:pw:v1",
  "mpp:about-preview:pw:v1",
  "mpp:buckets-preview:pw:v1",
  "mpp:daily-brief-preview:pw:v4",
  "mpp:macro:pw:v1",
  "mpp:seasonality:pw:v1",
  "mpp:breadth-1:pw:v1",
  "mpp:charting:pw:v1",
  "mpp:fundamentals:pw:v1",
  "mpp:ratio:pw:v1",
  "mpp:guppy:pw:v1",
  "mpp:options:pw:v1",
  "mpp:options:trade:pw:v1",
  "mpp:options:analyze:pw:v1",
  "mpp:options:flow:pw:v1",
  "mpp:options:scan:pw:v1",
  "mpp:vpa:pw:v1",
  "mpp:index-etfs:pw:v1",
  "mpp:timing-brief:v2",
  "mpp:commodities-etfs:pw:v1",
  "mpp:outlook:pw:v1",
  // Wealth › Forecast, a workspace of panes since 2026-10-04. (The other
  // Wealth tabs name their keys under app/wealth/, and are found in the
  // source namespace like any page added later.)
  "mpp:wealth-forecast:pw:v1",
  "mpp:bets:pw:v1",
  "mpp:backtest:pw:v1",
  "mpp:minervini:pw:v1",
  "mpp:candles:pw:v1",
  "mpp:cot:pw:v1",
  "mpp:cycles:pw:v1",
  "mpp:stockbee:pw:v1",
  "mpp:market-monitor:pw:v1",
  "mpp:f13:pw:v1",
  "mpp:calendar:pw:v1",
  "mpp:settings-data-feed:pw:v1",
  "mpp:settings-universe:pw:v1",
  "mpp:settings-breadth:pw:v1",
  "mpp:settings-pages:pw:v1",
  "mpp:settings-indicators:pw:v1",
  "mpp:settings-theme:pw:v1",
  "mpp:settings-backup:pw:v1",
  "mpp:settings-maintenance:pw:v1",
  "mpp:help:pw:v1",
  "mpp:about:pw:v1",
] as const;

function newId(): string {
  return `dp${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

function isSourceRetina(p: DisplayProfile): boolean {
  const n = p.name.toLowerCase();
  if (/arranged|ruhi/.test(n)) return false;
  return /macbook/.test(n) && /retina/.test(n);
}

function isSamsung49(p: DisplayProfile): boolean {
  return /samsung\s*49/.test(p.name.toLowerCase());
}

function parseTree(raw: string | null): WsNode | null {
  if (!raw) return null;
  try {
    const root = JSON.parse(raw) as WsNode;
    return root && (root.t === "leaf" || root.t === "split") ? root : null;
  } catch {
    return null;
  }
}

function parseGrid(raw: string | null): LaptopGrid | null {
  if (!raw) return null;
  try {
    const v: unknown = JSON.parse(raw);
    return isLaptopGrid(v) ? v : null;
  } catch {
    return null;
  }
}

export function isPageGridKey(k: string): boolean {
  if (k.startsWith("mpp:nested-ws:") || k.startsWith("mpp:ws-tree:") || k.startsWith("mpp:ws:")) {
    return false;
  }
  if (k.endsWith("-locked") || k.includes(":cols:") || k.includes(":ind:")) return false;
  if ((PAGE_GRID_KEYS as readonly string[]).includes(k)) return true;
  return /^mpp:[a-z0-9-]+:pw:v\d+$/.test(k) || /^mpp:(daily-brief|timing-brief):v\d+$/.test(k);
}

function preferredTree(
  pageId: string,
  sourceId: string,
  refId: string | null,
  cloneId?: string,
): WsNode | null {
  // Keep a tree the clone already arranged (seed 1) so a bump does not
  // clobber laptop edits; otherwise Samsung 49 is the inventory.
  if (cloneId) {
    const own = parseTree(readNamespaced(cloneId, treeKey(pageId)));
    if (own) return own;
  }
  if (refId) {
    const fromRef = parseTree(readNamespaced(refId, treeKey(pageId)));
    if (fromRef) return fromRef;
  }
  return parseTree(readNamespaced(sourceId, treeKey(pageId)));
}

function preferredGrid(
  logical: string,
  sourceId: string,
  refId: string | null,
): LaptopGrid | null {
  // Samsung 49 is the inventory of which RGL panes sit on the page.
  if (refId) {
    const fromRef = parseGrid(readNamespaced(refId, logical));
    if (fromRef) return fromRef;
  }
  const fromSrc = parseGrid(readNamespaced(sourceId, logical));
  if (fromSrc) return fromSrc;
  return parseGrid(rawGet(logical));
}

function defaultPageGrid(storageKey: string, rows: number): LaptopGrid {
  const i = `${storageKey}:ws`;
  return {
    panes: [{ i, type: "pageWorkspace" }],
    layouts: { lg: [{ i, x: 0, y: 0, w: 12, h: rows, minW: 4, minH: 8 }] },
  };
}

function collectGridKeys(sourceId: string, refId: string | null, cloneId: string): string[] {
  const keys = new Set<string>(PAGE_GRID_KEYS);
  for (const id of [sourceId, refId, cloneId]) {
    if (!id) continue;
    for (const k of namespaceLogicalKeys(id)) {
      if (isPageGridKey(k)) keys.add(k);
    }
  }
  return [...keys];
}

function bandsForStorageKey(
  storageKey: string,
  cloneId: string,
  sourceId: string,
  refId: string | null,
  pages: ReturnType<typeof loadPages>,
): number {
  const activeKey = `mpp:nested-ws:${storageKey}:ws:active:v1`;
  const activeId =
    readNamespaced(cloneId, activeKey) ??
    (refId ? readNamespaced(refId, activeKey) : null) ??
    readNamespaced(sourceId, activeKey);
  const page = pages.find((p) => p.id === activeId);
  const tree = page ? (preferredTree(page.id, sourceId, refId, cloneId) ?? page.root) : null;
  return suggestedGridRows(tree);
}

function sameGridPanes(a: LaptopGrid, b: LaptopGrid): boolean {
  const ids = (g: LaptopGrid) =>
    g.panes
      .map((p) => `${p.i}\0${p.type}`)
      .sort()
      .join("|");
  return ids(a) === ids(b);
}

/**
 * Create / refresh the arranged Retina clone. Safe to call after hydrate;
 * never throws to the render gate (caller already guards).
 */
export function ensureRetinaArrangedProfile(): DisplayProfile | null {
  if (typeof window === "undefined") return null;
  const list = loadProfiles();
  const source = list.find(isSourceRetina);
  if (!source) return null;
  const ref = list.find(isSamsung49) ?? null;

  let clone = list.find((p) => p.name === RETINA_ARRANGED_NAME);
  let nextList = list;
  if (!clone) {
    clone = {
      id: newId(),
      name: RETINA_ARRANGED_NAME,
      updatedAt: Date.now(),
      screen: source.screen,
      scrollCanvas: true,
      treesSeeded: 0,
    };
    seedNamespaceFrom(source.id, clone.id);
    if (ref) {
      for (const logical of namespaceLogicalKeys(ref.id)) {
        if (readNamespaced(clone.id, logical) != null) continue;
        const v = readNamespaced(ref.id, logical);
        if (v != null) writeNamespaced(clone.id, logical, v);
      }
    }
    nextList = [...list, clone];
    saveProfiles(nextList);
  } else if (!clone.scrollCanvas) {
    clone = { ...clone, scrollCanvas: true };
    nextList = list.map((p) => (p.id === clone!.id ? clone! : p));
    saveProfiles(nextList);
  }

  if ((clone.treesSeeded ?? 0) >= RETINA_TREE_SEED) return clone;

  const pages = loadPages();
  // A seed bump must not clobber laptop edits already written into the clone.
  // A brand-new clone still takes Samsung 49 as the inventory.
  const reuseCloneTrees = (clone.treesSeeded ?? 0) >= 1;
  for (const page of pages) {
    const base =
      preferredTree(page.id, source.id, ref?.id ?? null, reuseCloneTrees ? clone.id : undefined) ??
      page.root;
    const reflowed = reflowForLaptop(base, suggestedLaptopCols(base));
    // Guard: never drop a component the source page had.
    const before = flattenComponents(base).filter(Boolean).sort().join("\0");
    const after = flattenComponents(reflowed).filter(Boolean).sort().join("\0");
    if (before !== after) continue;
    writeNamespaced(clone.id, treeKey(page.id), JSON.stringify(reflowed));
  }

  for (const key of collectGridKeys(source.id, ref?.id ?? null, clone.id)) {
    const rows = bandsForStorageKey(key, clone.id, source.id, ref?.id ?? null, pages);
    const base = preferredGrid(key, source.id, ref?.id ?? null) ?? defaultPageGrid(key, rows);
    const reflowed = reflowGridLayout(base, rows);
    if (!sameGridPanes(base, reflowed)) continue;
    writeNamespaced(clone.id, key, JSON.stringify(reflowed));
  }

  clone = { ...clone, treesSeeded: RETINA_TREE_SEED, updatedAt: Date.now() };
  saveProfiles(nextList.map((p) => (p.id === clone!.id ? clone! : p)));
  return clone;
}
